import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@template-name/trpc-types";
import { trpc } from "@/trpc/trpc";
import { formatDateTime, zl } from "@/components/subscription-labels";

type PlanVersion = inferRouterOutputs<AppRouter>["subscription"]["planVersions"][number];

const CHANGE_LABEL: Record<string, string> = {
  created: "Utworzono plan",
  plan_updated: "Zmieniono ustawienia planu",
  status_changed: "Opublikowano lub zarchiwizowano",
  entitlement_added: "Dodano świadczenie",
  entitlement_updated: "Zmieniono świadczenie",
  entitlement_removed: "Usunięto świadczenie",
};

const INTERVAL: Record<string, string> = {
  month: "miesięcznie",
  quarter: "kwartalnie",
  year: "rocznie",
  once: "jednorazowo",
};

const STATUS: Record<string, string> = {
  draft: "szkic",
  active: "opublikowany",
  archived: "zarchiwizowany",
};

/** One line of the terms, as the panel prints it. */
type Line = { key: string; label: string; value: string | null };

function planLines(p: PlanVersion["plan"]): Line[] {
  const fmt = <T,>(v: T | null | undefined, f: (x: T) => string) =>
    v === null || v === undefined ? null : f(v);
  return [
    { key: "name", label: "Nazwa", value: p.name ?? null },
    {
      key: "price",
      label: "Cena",
      value: fmt(p.price, (v) =>
        `${zl(v)}${p.billingInterval ? ` ${INTERVAL[p.billingInterval] ?? p.billingInterval}` : ""}`,
      ),
    },
    {
      key: "discountPercent",
      label: "Rabat na resztę oferty",
      value: fmt(p.discountPercent, (v) => `${v}%`),
    },
    {
      key: "creditsExpireAfterCycles",
      label: "Świadczenia przenoszą się",
      value: fmt(p.creditsExpireAfterCycles, (v) => `${v} okresów`),
    },
    {
      key: "minCommitmentCycles",
      label: "Zobowiązanie",
      value: fmt(p.minCommitmentCycles, (v) => (v > 0 ? `${v} okresów` : "brak")),
    },
    { key: "status", label: "Status", value: fmt(p.status, (v) => STATUS[v] ?? v) },
    { key: "badgeLabel", label: "Etykieta", value: p.badgeLabel ?? null },
    { key: "description", label: "Opis", value: p.description ?? null },
  ];
}

function countBy(lines: string[]) {
  const counts = new Map<string, number>();
  for (const l of lines) counts.set(l, (counts.get(l) ?? 0) + 1);
  return counts;
}

function entitlementLine(e: PlanVersion["entitlements"][number]) {
  return `${e.quantityPerCycle} × ${e.name}${
    e.maxRedemptionValue !== null ? ` do ${zl(e.maxRedemptionValue)}` : ""
  }${e.addonIncluded ? ", z dodatkami" : ""}`;
}

/**
 * The plan's history, newest first. Each version shows its full terms and
 * marks what differs from the version before it, so "what did it say in
 * March" and "what changed in April" are both one glance.
 */
export function PlanVersionsDialog({
  planId,
  planName,
  onClose,
}: {
  planId: string;
  planName: string;
  onClose: () => void;
}) {
  const { data, isLoading } = trpc.subscription.planVersions.useQuery({ planId });
  const versions = data ? [...data].reverse() : [];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Historia planu „{planName}"</DialogTitle>
          <DialogDescription>
            Każda zmiana ustawień lub świadczeń tworzy nową wersję. Klientka płaci
            i dostaje dokładnie to, co obowiązywało w wersji, którą kupiła.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {isLoading && <p className="text-sm text-muted-foreground">Ładowanie...</p>}

          <ol className="space-y-4">
            {versions.map((v, i) => {
              const prev = versions[i + 1];
              const prevLines = prev ? new Map(planLines(prev.plan).map((l) => [l.key, l.value])) : null;
              // Occurrence counts, not sets: a plan may legitimately list the
              // same benefit twice, and removing one of them is a change.
              const prevEnts = prev ? countBy(prev.entitlements.map(entitlementLine)) : null;
              const currEnts = countBy(v.entitlements.map(entitlementLine));
              const added = new Map<string, number>();
              if (prevEnts) {
                for (const [line, n] of currEnts) {
                  const extra = n - (prevEnts.get(line) ?? 0);
                  if (extra > 0) added.set(line, extra);
                }
              }
              const removed: string[] = [];
              if (prevEnts) {
                for (const [line, n] of prevEnts) {
                  const missing = n - (currEnts.get(line) ?? 0);
                  for (let k = 0; k < missing; k++) removed.push(line);
                }
              }
              const seen = new Map<string, number>();
              return (
                <li key={v.version} className="rounded-md border p-4">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">Wersja {v.version}</span>
                      {i === 0 && <Badge>obecna</Badge>}
                      {v.source === "reconstructed" && (
                        <Badge variant="outline" title="Sprzed wprowadzenia historii; odtworzona z warunków sprzedanych abonamentów">
                          odtworzona
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatDateTime(v.recordedAt)}
                      {v.changedBy && ` · ${v.changedBy}`}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {v.change ? CHANGE_LABEL[v.change] ?? v.change : "Warunki z tego okresu"}
                    {" · "}
                    {v.subscriptionsSold === 0
                      ? "nikt nie kupił tej wersji"
                      : `${v.subscriptionsSold} ${
                          v.subscriptionsSold === 1 ? "abonament sprzedany" : "abonamentów sprzedanych"
                        } w tej wersji`}
                  </p>

                  <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-3">
                    {planLines(v.plan)
                      // A field cleared in this version still has to appear,
                      // so the clearing is visible.
                      .filter((l) => l.value !== null || (prevLines?.get(l.key) ?? null) !== null)
                      .map((l) => {
                        const before = prevLines?.get(l.key) ?? null;
                        const changed = prevLines !== null && before !== l.value;
                        return (
                          <div key={l.key} className={changed ? "rounded bg-amber-50 px-1 -mx-1" : ""}>
                            <dt className="text-xs text-muted-foreground">{l.label}</dt>
                            <dd className="font-medium">
                              {l.value ?? <span className="text-muted-foreground">usunięto</span>}
                              {changed && before !== null && (
                                <span className="ml-1 text-xs font-normal text-muted-foreground line-through">
                                  {before}
                                </span>
                              )}
                            </dd>
                          </div>
                        );
                      })}
                  </dl>

                  <div className="mt-3">
                    <p className="text-xs text-muted-foreground">Co zawiera</p>
                    {v.entitlements.length === 0 && removed.length === 0 ? (
                      <p className="text-sm">brak świadczeń</p>
                    ) : (
                      <ul className="text-sm">
                        {v.entitlements.map((e) => {
                          const line = entitlementLine(e);
                          const nth = (seen.get(line) ?? 0) + 1;
                          seen.set(line, nth);
                          // The last `extra` occurrences of a line are the new ones.
                          const total = currEnts.get(line) ?? 0;
                          const isNew = nth > total - (added.get(line) ?? 0);
                          return (
                            <li key={`${line}#${nth}`} className={isNew ? "rounded bg-amber-50 px-1 -mx-1" : ""}>
                              {line}
                              {isNew && <span className="ml-1 text-xs text-muted-foreground">(nowe)</span>}
                            </li>
                          );
                        })}
                        {removed.map((line, k) => (
                          <li key={`removed-${line}#${k}`} className="text-muted-foreground line-through">
                            {line}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
